import React, { useState, useRef, useEffect, useMemo } from 'react';
import { M } from '@dashboard-experience/mastodon';
import { i18n } from '@international/mastodon-i18n';
import T from 'components/T';
import { DocumentStatus, DocumentStatusParams } from './utils/status';
import {
  OcrDocumentSectionProps,
  DeleteButtonProps,
  DocumentInfoProps,
  UpdateButtonProps,
  IconButtonProps,
  FailureReason,
} from './utils/interfaces';
import {
  OcrRequestOutcome,
  useEmploymentDocumentOcr,
  useEmploymentDocumentOcrResult,
} from './utils/hooks';
import './AiDocReview.scss';
import {
  checkOcrResults,
  getDocumentStatus,
  handleSuccessResult,
} from './utils/helpers';
import {
  isFourDigitYearOrInterval,
  getOcrPlaceholder,
  getDocumentOptions,
  getFileLabel,
  updateStateArray,
} from './utils/formatter';
import { CandidateContextField } from './CandidateContext';

const OCR_TIMEOUT = 45000;
const SLIDE_DURATION = 300;
const MAX_POLLS = 9;

const I18N_PREFIX = 'components.Verification.AiDocReview';

// Row variants drive the on-flag (OCR transient) presentation. Off-flag rows
// always fall through to `Legacy`.
export const ROW_VARIANT = Object.freeze({
  Editable: 'editable',
  InlineVerified: 'inline-verified',
  InlineError: 'inline-error',
  Legacy: 'legacy',
});

// Non-fixable statuses short-circuit BEFORE the `inEditMode` check: the OCR
// pipeline may legitimately leave `inEditMode = true` on a terminal
// (`Unreadable` is the canonical case), but every non-fixable row renders as
// a trash-only file-type-not-accepted card with no editor. Keying off
// `fixable` keeps this in lockstep with the `surfacedInEditMode` carve-out
// surfaced to the modal. `Edited` is fixable, so it falls through to the
// Editable variant for manual fill.
export const getRowVariant = ({
  status,
  inEditMode,
  ocrEligible,
  isPending,
}: {
  status: DocumentStatus;
  inEditMode: boolean;
  ocrEligible: boolean;
  isPending: boolean;
}) => {
  if (!ocrEligible || isPending) return ROW_VARIANT.Legacy;
  const params = DocumentStatusParams[status] as any;
  if (params && params.fixable === false) return ROW_VARIANT.InlineError;
  if (inEditMode) return ROW_VARIANT.Editable;
  if (status === DocumentStatus.Verified) return ROW_VARIANT.InlineVerified;
  return ROW_VARIANT.Legacy;
};

// The Editable variant now serves only the truly unclassifiable case (Extend
// could not identify the type). The year-out-of-range sub-case is locked as an
// InlineError (EVERI-3428), so its former branch here is unreachable.
export const getEditableCopy = () => ({
  titleKey: `${I18N_PREFIX}.unclassifiableTitle`,
  helpKey: `${I18N_PREFIX}.unclassifiableHelp`,
});

// EVERI-3896: `failure_reason` is on the wire (see OcrResultsResponse) but was
// never read — every terminal extraction failure fell through to the
// `fileTypeNotAccepted` default below regardless of what actually went wrong.
// This maps the reasons that need their own copy; `unrecognized_document_type`
// is intentionally absent so it keeps falling through to the existing
// (correct) file-type-not-accepted message.
export const FAILURE_REASON_COPY: Partial<
  Record<
    Exclude<FailureReason, null>,
    { titleKey: string; helpKey: string; showRetry: boolean }
  >
> = {
  invalid_document_structure: {
    titleKey: `${I18N_PREFIX}.multipleDocumentsTitle`,
    helpKey: `${I18N_PREFIX}.multipleDocumentsHelp`,
    showRetry: false,
  },
  insufficient_extraction: {
    titleKey: `${I18N_PREFIX}.missingDetailsTitle`,
    helpKey: `${I18N_PREFIX}.missingDetailsHelp`,
    showRetry: false,
  },
  inconsistent_document_type: {
    titleKey: `${I18N_PREFIX}.missingDetailsTitle`,
    helpKey: `${I18N_PREFIX}.missingDetailsHelp`,
    showRetry: false,
  },
  failed_to_process: {
    titleKey: `${I18N_PREFIX}.processingFailedTitle`,
    helpKey: `${I18N_PREFIX}.processingFailedHelp`,
    showRetry: true,
  },
};

// All inline-error statuses (mismatch + file-type-not-accepted) pair with the
// shared `mismatchHelp` subtitle. `Unreadable` / `Unverified` share the
// file-type-not-accepted title interpolating the accepted-types list, unless
// `failureReason` maps to a more specific message (EVERI-3896).
//
// `Unverified` has a watchdog-timeout sub-case (the OCR backend didn't answer
// in time, but the doc itself may be valid). There we surface a friendlier
// title + the Retry button rather than "doesn't look like a W-2…" so the
// candidate can re-run OCR (or trash the row).
// EVERI-4005: mirrors apply MR !3003 — neutral title on every status, reason demoted to help.
const getSoftenedErrorCopy = ({
  status,
  ocrTimeout,
  ocrTimeoutRetried,
  failureReason = null,
}: {
  status: DocumentStatus;
  ocrTimeout: boolean;
  ocrTimeoutRetried: boolean;
  failureReason?: FailureReason;
}) => {
  const titleKey = `${I18N_PREFIX}.softenedTitle`;
  if (
    status === DocumentStatus.Unverified &&
    ocrTimeout &&
    !ocrTimeoutRetried
  ) {
    return {
      titleKey,
      helpKey: `${I18N_PREFIX}.softenedTimeoutHelp`,
      showHelp: true,
      showRetry: true,
    };
  }
  switch (status) {
    case DocumentStatus.CandidateUnverified:
      return {
        titleKey,
        helpKey: `${I18N_PREFIX}.softenedCandidateMismatch`,
        showHelp: true,
        showRetry: false,
      };
    case DocumentStatus.EmployerUnverified:
      return {
        titleKey,
        helpKey: `${I18N_PREFIX}.softenedEmployerMismatch`,
        showHelp: true,
        showRetry: false,
      };
    case DocumentStatus.NeitherVerified:
      return {
        titleKey,
        helpKey: `${I18N_PREFIX}.softenedNeitherMismatch`,
        showHelp: true,
        showRetry: false,
      };
    case DocumentStatus.YearOutOfRange:
      return {
        titleKey,
        helpKey: `${I18N_PREFIX}.softenedYearNotInRange`,
        showHelp: true,
        showRetry: false,
      };
    case DocumentStatus.Unreadable:
    case DocumentStatus.Unverified:
    default: {
      // Bugfix mirrored from apply acba2ada: this default branch never read
      // failureReason, so a failed_to_process terminal lost its Retry button
      // (and now, EVERI-4122, its Submit block) whenever the softened card is
      // showing — copy stays generic by design, only showRetry should track
      // the real reason.
      const mapped = failureReason
        ? FAILURE_REASON_COPY[failureReason]
        : undefined;
      return {
        titleKey,
        helpKey: `${I18N_PREFIX}.softenedFileTypeNotAccepted`,
        showHelp: true,
        showRetry: Boolean(mapped?.showRetry),
      };
    }
  }
};

export const getInlineErrorCopy = ({
  status,
  ocrTimeout = false,
  ocrTimeoutRetried = false,
  softened = false,
  failureReason = null,
}: {
  status: DocumentStatus;
  ocrTimeout?: boolean;
  ocrTimeoutRetried?: boolean;
  softened?: boolean; // EVERI-4005: neutral title + all-status softened help, matching apply
  failureReason?: FailureReason;
}) => {
  if (softened)
    return getSoftenedErrorCopy({
      status,
      ocrTimeout,
      ocrTimeoutRetried,
      failureReason,
    });
  if (status === DocumentStatus.Unverified && ocrTimeout) {
    return {
      titleKey: `${I18N_PREFIX}.timeoutTitle`,
      helpKey: `${I18N_PREFIX}.timeoutHelp`,
      showHelp: true,
      showRetry: true,
    };
  }
  switch (status) {
    case DocumentStatus.CandidateUnverified:
      return {
        titleKey: `${I18N_PREFIX}.candidateMismatchTitle`,
        helpKey: '',
        showHelp: true,
        showRetry: false,
      };
    case DocumentStatus.EmployerUnverified:
      return {
        titleKey: `${I18N_PREFIX}.employerMismatchTitle`,
        helpKey: '',
        showHelp: true,
        showRetry: false,
      };
    case DocumentStatus.NeitherVerified:
      return {
        titleKey: `${I18N_PREFIX}.neitherMismatchTitle`,
        helpKey: '',
        showHelp: true,
        showRetry: false,
      };
    case DocumentStatus.YearOutOfRange:
      return {
        titleKey: `${I18N_PREFIX}.yearOutOfRangeErrorTitle`,
        helpKey: `${I18N_PREFIX}.yearOutOfRangeErrorHelp`,
        showHelp: true,
        showRetry: false,
      };
    case DocumentStatus.Unreadable:
    case DocumentStatus.Unverified:
    default: {
      const mapped = failureReason
        ? FAILURE_REASON_COPY[failureReason]
        : undefined;
      if (mapped) {
        return {
          titleKey: mapped.titleKey,
          helpKey: mapped.helpKey,
          showHelp: true,
          showRetry: mapped.showRetry,
        };
      }
      return {
        titleKey: `${I18N_PREFIX}.fileTypeNotAccepted`,
        helpKey: '',
        showHelp: true,
        showRetry: false,
      };
    }
  }
};

interface OcrProgressBarProps {
  timeoutMs: number;
  ocrSuccessRef: React.MutableRefObject<boolean | null>;
  onComplete: () => void;
}

// Self-contained scanning progress bar. It owns its own rAF loop + progress
// state so the per-frame updates re-render only this leaf, never the parent row
// (which carries the dropdowns and buttons) — that isolation is what keeps the
// fill smooth under load. Renders mastodon's `M.ProgressBar` for design-system
// parity: `bg` is the track, `bgInner`/`bgComplete` the blue fill. On reaching
// 100% it calls `onComplete` after a short settle so the result row can swap in.
const OcrProgressBar: React.FC<OcrProgressBarProps> = ({
  timeoutMs,
  ocrSuccessRef,
  onComplete,
}) => {
  const [progress, setProgress] = useState(0);
  const progressRef = useRef(0);
  const startTimeRef = useRef(Date.now());
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    let animationFrameId: number;
    let terminalTimeoutId: ReturnType<typeof setTimeout>;

    const tick = () => {
      let next = ((Date.now() - startTimeRef.current) / timeoutMs) * 100;
      // A confirmed OCR failure fast-forwards the bar to 100% rather than
      // waiting out the full watchdog window.
      if (ocrSuccessRef.current === false) {
        next = progressRef.current + 0.5;
      }

      if (next >= 100) {
        progressRef.current = 100;
        setProgress(100);
        terminalTimeoutId = setTimeout(
          () => onCompleteRef.current(),
          2 * SLIDE_DURATION,
        );
        return;
      }

      progressRef.current = next;
      setProgress(next);
      animationFrameId = requestAnimationFrame(tick);
    };

    animationFrameId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(animationFrameId);
      clearTimeout(terminalTimeoutId);
    };
  }, [timeoutMs, ocrSuccessRef]);

  return (
    <M.ProgressBar
      bg='#F2F5F7'
      bgInner='#0A57A4'
      bgComplete='#0A57A4'
      progress={progress}
    />
  );
};

const OcrDocumentSection: React.FC<OcrDocumentSectionProps> = ({
  files,
  screeningId,
  onRemoveByUploadId,
  aiOcrEnabled,
  dropdown,
  handleDocumentTypeChange,
  handleYearChange,
  documentTypes,
  state,
  i,
  company,
  fullName,
  acceptedTypesText,
  setState,
  candidateDocContextEnabled = false,
}) => {
  const [ocr, setOcr] = useState({
    inEditMode: true,
    readable: true,
    completed: false,
    candidateVerified: false,
    employerVerified: false,
    success: null as boolean | null,
    timeout: false,
    // True once the candidate has clicked Retry on a timed-out row; a second
    // timeout then routes to manual-fill (dropdowns) instead of Retry again.
    timeoutRetried: false,
    // OCR resolved a doc type but the extracted year doesn't satisfy any
    // required slot. Drives the locked InlineError "year not in required
    // range" card (EVERI-3428).
    yearOutOfRange: false,
    // Display label of the recognized doc type (e.g. "W-2"), threaded into the
    // year-out-of-range InlineError title interpolation.
    ocrDocTypeLabel: '',
    // Wire-provided reason a terminal extraction failed (EVERI-3896), threaded
    // into getInlineErrorCopy so the InlineError card matches what actually
    // went wrong instead of always showing "doesn't look like a %{types}".
    failureReason: null as FailureReason,
  });

  const file = files[i];
  const isLastElement = files.length === 1;

  const otherType = getDocumentOptions(documentTypes).find(
    opt => opt.value === 'EmploymentProof',
  );

  const selectedDocumentType = state.selectedDocumentTypes[i];
  const selectedYear = state.selectedYears[i];

  const stateRef = useRef(state);
  stateRef.current = state;
  const hasFailedRef = useRef(false);
  const hasVerifiedRef = useRef(false);
  const ocrSuccessRef = useRef(ocr.success);
  const failureCountRef = useRef(0);

  const { sendOcrRequestsForFile } = useEmploymentDocumentOcr();
  const { getOcrResult } = useEmploymentDocumentOcrResult();

  // Must fire on every failure, not just the first: a retry resets
  // hasFailedRef to false, and a second failed_to_process needs to re-set it
  // so getDocumentStatus still classifies the row as failed instead of
  // falling through to manual-fill or a name mismatch (Bugbot).
  const markExtractionFailed = (failureReason: FailureReason) => {
    hasFailedRef.current = true;
    failureCountRef.current += 1;
    setOcr(prev => ({ ...prev, failureReason }));
  };

  // A POST that fails up front never starts polling, so it has to mark the
  // row failed itself, the same way a failed poll terminal does. Otherwise the
  // still-readable, unverified row reads as a NeitherVerified name mismatch.
  const applyOcrRequestOutcome = ({
    success,
    failureReason,
  }: OcrRequestOutcome) => {
    if (!success) {
      handleDocumentTypeChange({ ...otherType, uploadId: file.uploadId });
      markExtractionFailed(failureReason);
    }
    setOcr(prev => ({ ...prev, success }));
  };

  useEffect(() => {
    if (!ocr.success || !aiOcrEnabled) return () => {};

    // Extracted from the setInterval callback so the setOcr updater below is
    // not nested more than 4 functions deep (SonarQube typescript:S2004).
    const pollOcrResult = async (
      intervalId: ReturnType<typeof setInterval>,
    ) => {
      const polledResult = await getOcrResult(file.uploadId, file.filename);
      const ocrResult = checkOcrResults(polledResult);
      setOcr(prev => ({
        ...prev,
        candidateVerified: ocrResult.isCandidateNameVerified,
        employerVerified: ocrResult.isEmployerNameVerified,
      }));
      hasVerifiedRef.current =
        failureCountRef.current >= 1 || ocrResult.isVerified;

      if (ocrResult.succeededResult || ocrResult.failedResult) {
        handleDocumentTypeChange({ ...otherType, uploadId: file.uploadId });
        setOcr(prev => ({ ...prev, success: false }));
        clearInterval(intervalId);
      }

      if (ocrResult.failedResult) {
        markExtractionFailed(ocrResult.failureReason);
      }

      if (ocrResult.succeededResult) {
        await handleSuccessResult(
          ocrResult,
          file,
          i,
          documentTypes,
          setState,
          setOcr,
          stateRef,
          handleDocumentTypeChange,
          handleYearChange,
        );
      }
    };

    const intervalId = setInterval(() => {
      pollOcrResult(intervalId);
    }, OCR_TIMEOUT / MAX_POLLS);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ocr.success]);

  useEffect(() => {
    if (!aiOcrEnabled) {
      setOcr(prev => ({ ...prev, completed: true }));
      return;
    }

    // No type pre-seed: leaving it unset lets the Extend-down/manual-fill path show
    // both type+year dropdowns; OCR success/fail still set the type below.
    sendOcrRequestsForFile(file, screeningId).then(applyOcrRequestOutcome);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Runs when the scanning bar reaches 100% (the watchdog ceiling). The bar
  // itself lives in <OcrProgressBar/>, which owns the rAF animation and calls
  // this once it tops out — keeping the per-frame churn out of this row.
  const handleProgressComplete = () => {
    setOcr(prevOcr => ({ ...prevOcr, completed: true }));
    updateStateArray(setState, 'hasOcrInProgress', true, i);
    if (ocrSuccessRef.current === true) {
      setOcr(prevOcr => ({ ...prevOcr, timeout: true, success: false }));
    }

    if (hasFailedRef.current || ocrSuccessRef.current !== false) {
      setOcr(prevOcr => {
        // First timeout hides dropdowns (Retry); a post-retry timeout
        // (timeoutRetried) keeps inEditMode so manual-fill dropdowns show.
        const inEditMode =
          prevOcr.timeout && !prevOcr.timeoutRetried
            ? false
            : prevOcr.inEditMode;
        return { ...prevOcr, inEditMode, success: false };
      });
    }
  };

  const handleRetry = () => {
    // A watchdog timeout means the same upload may still resolve in the
    // background, so resuming polling is correct. A retry driven by a
    // wire-provided `failureReason` (currently only `failed_to_process`,
    // the only FAILURE_REASON_COPY entry with `showRetry: true`) means the
    // backend already gave its final answer on this upload -- polling it
    // again would just return the same failure forever, so that case needs
    // a fresh `sendOcrRequestsForFile` call (below), not a re-poll.
    const isFailureRetry = Boolean(ocr.failureReason);
    setOcr(prev => ({
      ...prev,
      // Restore edit mode (cleared on the first timeout) so a second timeout
      // routes to manual-fill dropdowns rather than looping on Retry.
      inEditMode: true,
      readable: true,
      completed: false,
      // Timeout retry resumes polling immediately. A failure-reason retry
      // stays `null` (matching the mount effect's request-in-flight state)
      // until the re-request below resolves, so polling doesn't start
      // against a job that doesn't exist yet.
      success: isFailureRetry ? null : true,
      timeout: false,
      timeoutRetried: prev.timeout ? true : prev.timeoutRetried,
      yearOutOfRange: false,
      // A fresh extraction attempt shouldn't carry the previous failure's
      // reason forward.
      failureReason: null,
    }));
    hasFailedRef.current = false;
    hasVerifiedRef.current = false;
    // Clear the sticky failure counter too: the poll forces
    // `hasVerifiedRef.current = failureCountRef.current >= 1 || ...`, so a
    // leftover count would misclassify a fresh, name-mismatched extraction as
    // Verified instead of a mismatch after a failure-reason retry.
    failureCountRef.current = 0;
    // Setting completed:false above remounts <OcrProgressBar/>, which restarts
    // its own wall-clock from zero — no shared timer to reset here.
    updateStateArray(setState, 'hasOcrInProgress', false, i);

    if (isFailureRetry) {
      sendOcrRequestsForFile(file, screeningId).then(applyOcrRequestOutcome);
    }
  };

  const ocrStatus = useMemo(
    () =>
      getDocumentStatus(
        ocr,
        hasVerifiedRef.current,
        hasFailedRef.current,
        aiOcrEnabled,
      ),
    [ocr, aiOcrEnabled],
  );

  const triggerValidationError = (index: number) => {
    updateStateArray(
      setState,
      'hasIncompleteMode',
      Array(files.length).fill(false),
    );
    updateStateArray(setState, 'hasIncompleteMode', true, index);
  };

  const isPending = aiOcrEnabled && !ocr.completed;
  const variant = getRowVariant({
    status: ocrStatus,
    inEditMode: ocr.inEditMode,
    ocrEligible: aiOcrEnabled,
    isPending,
  });

  // EVERI-4005: matches apply MR !3003 — every InlineError status gets the softened card + context field.
  const showCandidateContext =
    candidateDocContextEnabled &&
    aiOcrEnabled &&
    ocr.completed &&
    variant === ROW_VARIANT.InlineError;

  // EVERI-4122 (Kristian): `failed_to_process` is the only failure reason with
  // an actionable Retry (a fresh attempt may come back clean), so it's the
  // only one that must block Submit until the candidate resolves it — same
  // idea as the open-editor gate below, just keyed off the error card instead
  // of the dropdown form. Pure function of the same inputs `DocumentInfo` uses
  // for its own title/help copy, so the two can't drift apart.
  const inlineErrorCopy =
    variant === ROW_VARIANT.InlineError
      ? getInlineErrorCopy({
          status: ocrStatus,
          ocrTimeout: ocr.timeout,
          ocrTimeoutRetried: ocr.timeoutRetried,
          softened: showCandidateContext,
          failureReason: ocr.failureReason,
        })
      : null;
  const showRetryInline = Boolean(inlineErrorCopy?.showRetry);

  // Non-fixable statuses (mismatch / Unverified / Unreadable) never count as
  // an open editor — they render as InlineError with no Save button, so the
  // raw `inEditMode` flag must not latch the modal's Submit off.
  const isFixableStatus =
    (DocumentStatusParams[ocrStatus] as any)?.fixable !== false;
  const surfacedInEditMode = ocr.inEditMode && isFixableStatus;

  useEffect(() => {
    setState((prevState: any) => {
      const inEditMode = [...(prevState.inEditMode || [])];
      inEditMode[i] = surfacedInEditMode;
      const hasUnresolvedFailure = [...(prevState.hasUnresolvedFailure || [])];
      hasUnresolvedFailure[i] = showRetryInline;
      return {
        ...prevState,
        statuses: {
          ...prevState.statuses,
          [i]: ocrStatus,
        },
        inEditMode,
        hasUnresolvedFailure,
      };
    });
    ocrSuccessRef.current = ocr.success;
  }, [
    ocrStatus,
    i,
    setState,
    ocr.success,
    surfacedInEditMode,
    showRetryInline,
  ]);

  // Dropdowns are suppressed on InlineError / InlineVerified so they don't
  // stack under the collapsed card.
  const showDropdowns =
    ocr.completed &&
    ocr.inEditMode &&
    variant !== ROW_VARIANT.InlineError &&
    variant !== ROW_VARIANT.InlineVerified;

  // Save is disabled until type is set AND (for required types) the year is a
  // valid 4-digit year / interval.
  const isRowValid = (() => {
    const typePicked = Boolean(selectedDocumentType?.value);
    if (!typePicked) return false;
    if (selectedDocumentType.requiredForVerification === false) return true;
    return isFourDigitYearOrInterval(selectedYear);
  })();

  const wrapperClass = [
    'document-details',
    variant === ROW_VARIANT.InlineError ? 'document-details--alert' : '',
    showCandidateContext ? 'document-details--alert-soft' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapperClass} key={file.uploadId}>
      <DocumentInfo
        doc={file}
        status={ocrStatus}
        onRemoveByUploadId={onRemoveByUploadId}
        setOcr={setOcr}
        selectedDocumentType={selectedDocumentType}
        selectedYear={selectedYear}
        company={company}
        fullName={fullName}
        handleRetry={handleRetry}
        isLastElement={isLastElement}
        ocr={ocr}
        aiOcrEnabled={aiOcrEnabled}
        variant={variant}
        acceptedTypesText={acceptedTypesText}
        showCandidateContext={showCandidateContext}
      />
      {ocr.completed ? (
        showDropdowns && (
          <div
            className={
              aiOcrEnabled ? 'dropdown-row dropdown-row--ocr' : 'dropdown-row'
            }
          >
            {aiOcrEnabled && (
              <div className='dropdown-row__icon'>
                <M.Icon icon='Document' />
              </div>
            )}
            {dropdown}
            <UpdateButton
              setOcr={setOcr}
              selectedDocumentType={selectedDocumentType}
              selectedYear={selectedYear}
              onInvalid={() => triggerValidationError(i)}
              aiOcrEnabled={aiOcrEnabled}
              disabled={aiOcrEnabled && !isRowValid}
            />
            {aiOcrEnabled && (
              <DeleteButton
                onRemoveByUploadId={onRemoveByUploadId}
                isOcrCompleted={ocr.completed}
                doc={file}
              />
            )}
          </div>
        )
      ) : (
        <OcrProgressBar
          timeoutMs={OCR_TIMEOUT}
          ocrSuccessRef={ocrSuccessRef}
          onComplete={handleProgressComplete}
        />
      )}
      {ocr.completed && (
        <>
          <div className='ocr-separator' />
          <div className='file-info-row'>
            <p className='file-info'>{file.filename}</p>
            {/* EVERI-4122 (Kristian): rearranged out of the title row so the
                card reads less crowded — Retry now sits with the filename it
                acts on, trash stays the only action up top. */}
            {showRetryInline && <RetryButton handleRetry={handleRetry} />}
          </div>
        </>
      )}
      {showCandidateContext && (
        <CandidateContextField
          id={`candidate-context-${file.uploadId}`}
          value={state.candidateContext?.[i] ?? ''}
          onChange={value =>
            updateStateArray(setState, 'candidateContext', value, i)
          }
        />
      )}
    </div>
  );
};

const IconButton: React.FC<IconButtonProps> = ({
  icon,
  className = '',
  children,
  ...props
}) => (
  <button type='button' className={`icon-button ${className}`} {...props}>
    <M.Icon icon={icon} />
    {children}
  </button>
);

const DeleteButton: React.FC<DeleteButtonProps> = ({
  onRemoveByUploadId,
  isOcrCompleted,
  doc,
}) => {
  return isOcrCompleted ? (
    <IconButton
      icon='TrashCan'
      className='delete-file remove-icon'
      onClick={() => onRemoveByUploadId(doc.uploadId, doc.exceptionId)}
      data-testid='delete-file'
    />
  ) : (
    <IconButton
      icon='Close'
      className='delete-file'
      onClick={() => onRemoveByUploadId(doc.uploadId, doc.exceptionId)}
      data-testid='cancel-file'
    />
  );
};

const EditButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <IconButton
    icon='Edit'
    className='edit-file'
    data-testid='change-file'
    onClick={onClick}
  />
);

const DocumentIcon: React.FC<{ status: DocumentStatus }> = ({ status }) => {
  return (
    <div data-testid='success-icon' className={`${status}-icon`}>
      <M.Icon icon={DocumentStatusParams[status]?.icon} />
    </div>
  );
};

export const DocumentInfo: React.FC<DocumentInfoProps> = ({
  doc,
  status,
  onRemoveByUploadId,
  setOcr,
  selectedDocumentType,
  selectedYear,
  company,
  fullName,
  handleRetry,
  isLastElement,
  ocr,
  aiOcrEnabled = false,
  variant = ROW_VARIANT.Legacy,
  acceptedTypesText = '',
  showCandidateContext = false,
}) => {
  const placeholder = getOcrPlaceholder(selectedDocumentType, selectedYear);
  const params = (DocumentStatusParams[status] as any) || {};

  // OCR path shows trash on every row; legacy preserves the `!isLastElement`
  // guard so the non-OCR fallback can't trash-empty into an unrecoverable
  // state.
  const canDelete = aiOcrEnabled
    ? params.deleteable !== false
    : params.deleteable && !isLastElement;

  // Editable: title + helper here; the dropdown row + Save + trash are
  // rendered by `OcrDocumentSection` since it owns the form Fields.
  if (variant === ROW_VARIANT.Editable) {
    const { titleKey, helpKey } = getEditableCopy();
    return (
      <div className='document-info document-info--editable'>
        <p className='document-info__title'>{i18n.getStr(titleKey)}</p>
        <p className='document-info__help'>{i18n.getStr(helpKey)}</p>
      </div>
    );
  }

  // Inline-verified (auto-pass): green check + "{Type} for {Year}" +
  // auto-pass subtitle + trash. No edit pencil — candidates re-edit by
  // deleting and re-uploading.
  if (variant === ROW_VARIANT.InlineVerified) {
    return (
      <div className='document-info document-info--inline'>
        <div className='document-info__icon document-info__icon--verified'>
          <M.Icon icon='CheckmarkFilled' />
        </div>
        <div className='document-info__text'>
          <p className='document-info__title'>{placeholder}</p>
          <p className='document-info__help'>
            {i18n.getStr(`${I18N_PREFIX}.autoPassed`)}
          </p>
        </div>
        <div className='document-info__actions'>
          {canDelete && (
            <DeleteButton
              onRemoveByUploadId={onRemoveByUploadId}
              isOcrCompleted={ocr.completed}
              doc={doc}
            />
          )}
        </div>
      </div>
    );
  }

  // Inline-error (mismatch, file-type-not-accepted, or watchdog timeout):
  // orange card + bold title + subtitle + trash. Retry (timeout + failed-to-
  // process sub-cases) renders separately, alongside the filename at the
  // bottom of the card (EVERI-4122) — less crowded than stacking it next to
  // the title. `WarningAltFilled` is used over `ErrorFilled` because the
  // latter has a hardcoded red fill that overrides our SCSS.
  if (variant === ROW_VARIANT.InlineError) {
    const { titleKey, helpKey, showHelp } = getInlineErrorCopy({
      status,
      ocrTimeout: ocr.timeout,
      ocrTimeoutRetried: ocr.timeoutRetried,
      softened: showCandidateContext,
      failureReason: ocr.failureReason,
    });
    const resolvedHelpKey = helpKey || `${I18N_PREFIX}.mismatchHelp`;
    return (
      <div className='document-info document-info--inline document-info--error'>
        <div className='document-info__icon document-info__icon--error'>
          <M.Icon icon='WarningAltFilled' />
        </div>
        <div className='document-info__text'>
          <p className='document-info__title'>
            <T
              value={titleKey}
              fullName={fullName}
              company={company}
              types={acceptedTypesText}
              documentType={ocr.ocrDocTypeLabel}
              dangerousHTML
            />
          </p>
          {showHelp && (
            <p className='document-info__help'>
              {showCandidateContext ? (
                // EVERI-4005: only the new softened copy has %{fullName}/%{company} placeholders — unsoftened help keys are plain strings, unchanged from before.
                <T
                  value={resolvedHelpKey}
                  fullName={fullName}
                  company={company}
                  types={acceptedTypesText}
                  dangerousHTML
                />
              ) : (
                i18n.getStr(resolvedHelpKey)
              )}
            </p>
          )}
        </div>
        <div className='document-info__actions'>
          {canDelete && (
            <DeleteButton
              onRemoveByUploadId={onRemoveByUploadId}
              isOcrCompleted={ocr.completed}
              doc={doc}
            />
          )}
        </div>
      </div>
    );
  }

  // Legacy variant — non-OCR multi-doc fallback + the pending-OCR window.
  const fileLabel = getFileLabel(
    doc,
    status,
    company,
    fullName,
    ocr,
    placeholder,
  );
  // On-flag, saved/verified rows are delete-only (no edit pencil) — matches
  // apply EVERI-3320. This legacy branch is only reached on-flag in narrow
  // cases (e.g. an `Edited` status that isn't in edit mode), where we still
  // suppress the pencil.
  const editPencilVisible =
    ocr.completed &&
    !ocr.inEditMode &&
    params.verified !== false &&
    !aiOcrEnabled;

  return (
    <div className='success-message'>
      {DocumentStatusParams[status]?.icon && <DocumentIcon status={status} />}
      <div className='document-text-wrapper'>
        <p className='file-name'>{fileLabel}</p>
        {status === DocumentStatus.Unreadable && (
          <p className='file-info'>
            {i18n.getStr(`${I18N_PREFIX}.documentMustBe`)}
          </p>
        )}
      </div>
      <div className='actions'>
        {status === DocumentStatus.Unverified && (
          <RetryButton handleRetry={handleRetry} />
        )}
        {editPencilVisible && (
          <EditButton
            onClick={() =>
              setOcr((prev: any) => ({ ...prev, inEditMode: true }))
            }
          />
        )}
        {canDelete && (
          <DeleteButton
            onRemoveByUploadId={onRemoveByUploadId}
            isOcrCompleted={ocr.completed}
            doc={doc}
          />
        )}
      </div>
    </div>
  );
};

const LoadingMessage: React.FC<{ candidateDocContextEnabled?: boolean }> = ({
  candidateDocContextEnabled = false,
}) => {
  // EVERI-4248: singular copy once the flag caps the candidate to one document.
  const scanningKey = candidateDocContextEnabled
    ? `${I18N_PREFIX}.scanningSingle`
    : `${I18N_PREFIX}.scanning`;
  return (
    <div className='loading-spinner-container'>
      <div className='loading-spinner' />
      <div className='file-name'>{i18n.getStr(scanningKey)}</div>
    </div>
  );
};

const UpdateButton: React.FC<UpdateButtonProps> = ({
  setOcr,
  selectedDocumentType,
  selectedYear,
  onInvalid,
  aiOcrEnabled = false,
  disabled = false,
}) => {
  const handleUpdate = () => {
    if (disabled) return;
    if (
      !selectedDocumentType ||
      (selectedDocumentType.requiredForVerification &&
        !isFourDigitYearOrInterval(selectedYear))
    ) {
      onInvalid();
      return;
    }
    // Clear the year-out-of-range flag on save so a resolved row can't carry a
    // stale "we couldn't confirm the year" prompt.
    setOcr((prev: any) => ({
      ...prev,
      inEditMode: false,
      yearOutOfRange: false,
    }));
  };

  const buttonClass = [
    'update-button',
    disabled ? 'update-button--disabled' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <M.Button
      kind='secondary'
      className={buttonClass}
      onClick={handleUpdate}
      disabled={disabled}
    >
      {i18n.getStr(
        aiOcrEnabled ? `${I18N_PREFIX}.save` : `${I18N_PREFIX}.update`,
      )}
    </M.Button>
  );
};

const RetryButton: React.FC<{
  handleRetry: any;
}> = ({ handleRetry }) => {
  return (
    <M.Button
      kind='secondary'
      className='retry-ocr-button'
      onClick={handleRetry}
    >
      {i18n.getStr(`${I18N_PREFIX}.tryAgain`)}
    </M.Button>
  );
};

export { OcrDocumentSection, LoadingMessage };
